/**
 * Bundle Builder Component
 * - Arranges components matching Shopify Bundles app
 * - Handles custom swatches (variant.metafields.custom.swatches)
 * - Synchronizes directly with main PDP form, Add to Cart button, and Price
 */

if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.setup();
    }

    connectedCallback() {
      if (!this.initialized) {
        setTimeout(() => {
          this.setup();
        }, 0);
      }
    }

    setup() {
      this.sectionId = this.dataset.sectionId || '';
      this.productId = this.dataset.productId || '';

      this.components = Array.from(this.querySelectorAll('.bundle-component'));
      this.currentSelections = [];

      // Read parent bundle JSON data
      this.parentVariants = this.readJSONScript('[data-bundle-parent-variants]') || [];
      this.parentOptions = this.readJSONScript('[data-bundle-parent-options]') || [];
      this.settings = this.readJSONScript('[data-bundle-settings]') || {};

      if (!this.components.length) return;

      this.initialized = true;
      this.init();
    }

    readJSONScript(selector) {
      const script = this.querySelector(selector);
      if (script && script.textContent) {
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          console.error('BundleBuilder: Error parsing JSON script', selector, e);
        }
      }
      return null;
    }

    init() {
      if (!this.components.length) return;

      // 1. Establish initial selections for each configurable option
      this.components.forEach(component => {
        if (component.dataset.isFixed === 'true') return;

        const optionIdxStr = component.dataset.optionIndex;
        if (typeof optionIdxStr === 'undefined' || optionIdxStr === null) return;
        const optionIdx = parseInt(optionIdxStr, 10);
        if (isNaN(optionIdx)) return;
        
        // Check for active swatch first
        const activeSwatch = component.querySelector('[data-color-swatch].is-active');
        const select = component.querySelector('[data-variant-select]');

        let initialVal = '';
        if (activeSwatch && activeSwatch.dataset.optionValue) {
          initialVal = activeSwatch.dataset.optionValue;
        } else if (select && select.value) {
          initialVal = select.value;
        }

        this.currentSelections[optionIdx] = initialVal;

        // Initialize event listeners
        this.initComponentEvents(component, optionIdx);
      });

      // 2. Remove disabled attribute from PDP hidden input
      this.enablePDPForms();

      // 3. Immediately sync with PDP ATC & Price
      this.syncWithPDP();
    }

    initComponentEvents(component, optionIdx) {
      if (component._eventsBound) return;
      component._eventsBound = true;

      // Swatch clicks
      const swatches = component.querySelectorAll('[data-color-swatch]');
      swatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleSwatchClick(swatch, component, optionIdx);
        });
      });

      // Select dropdown changes
      const select = component.querySelector('[data-variant-select]');
      if (select) {
        select.addEventListener('change', (e) => {
          this.handleSelectChange(e.target, component, optionIdx);
        });
      }
    }

    handleSwatchClick(swatch, component, optionIdx) {
      const optionValue = swatch.dataset.optionValue;
      const variantTitle = swatch.dataset.variantTitle || optionValue;
      const imageSrc = swatch.dataset.imageSrc;
      const imageSrcset = swatch.dataset.imageSrcset;

      // 1. Update swatch active state
      const swatches = component.querySelectorAll('[data-color-swatch]');
      swatches.forEach(s => {
        s.classList.remove('is-active');
        s.setAttribute('tabindex', '-1');
      });
      swatch.classList.add('is-active');
      swatch.removeAttribute('tabindex');

      // 2. Sync with component dropdown
      const select = component.querySelector('[data-variant-select]');
      if (select && optionValue) {
        select.value = optionValue;
      }

      // 3. Update component image
      if (imageSrc) {
        const imageElement = component.querySelector('[data-component-image]');
        if (imageElement) {
          imageElement.src = imageSrc;
          if (imageSrcset) imageElement.srcset = imageSrcset;
        }
      }

      // 4. Update variant name display
      const variantNameEl = component.querySelector('[data-variant-name]');
      if (variantNameEl && variantTitle) {
        variantNameEl.textContent = variantTitle;
      }

      // 5. Update state & sync with PDP
      this.currentSelections[optionIdx] = optionValue;
      this.syncWithPDP();
    }

    handleSelectChange(select, component, optionIdx) {
      const selectedOption = select.options[select.selectedIndex];
      const optionValue = selectedOption.value;
      const variantTitle = selectedOption.dataset.variantTitle || optionValue;
      const imageSrc = selectedOption.dataset.imageSrc;
      const imageSrcset = selectedOption.dataset.imageSrcset;

      // 1. Update active swatch matching this value
      const swatches = component.querySelectorAll('[data-color-swatch]');
      swatches.forEach(swatch => {
        if (swatch.dataset.optionValue === optionValue) {
          swatch.classList.add('is-active');
          swatch.removeAttribute('tabindex');
        } else {
          swatch.classList.remove('is-active');
          swatch.setAttribute('tabindex', '-1');
        }
      });

      // 2. Update image
      if (imageSrc) {
        const imageElement = component.querySelector('[data-component-image]');
        if (imageElement) {
          imageElement.src = imageSrc;
          if (imageSrcset) imageElement.srcset = imageSrcset;
        }
      }

      // 3. Update variant name
      const variantNameEl = component.querySelector('[data-variant-name]');
      if (variantNameEl && variantTitle) {
        variantNameEl.textContent = variantTitle;
      }

      // 4. Update state & sync with PDP
      this.currentSelections[optionIdx] = optionValue;
      this.syncWithPDP();
    }

    /**
     * Find the matching bundle parent variant based on currentSelections
     */
    findMatchingParentVariant() {
      if (!this.parentVariants || !this.parentVariants.length) return null;

      // Match all options
      const matched = this.parentVariants.find(variant => {
        if (!variant.options || !variant.options.length) return false;

        return variant.options.every((optVal, idx) => {
          const selected = this.currentSelections[idx];
          if (!selected) return true; // Single option or omitted

          const cleanOpt = String(optVal).trim().toLowerCase();
          const cleanSel = String(selected).trim().toLowerCase();
          return cleanOpt === cleanSel;
        });
      });

      return matched || this.parentVariants[0];
    }

    getScope() {
      return this.closest('.quick-view') || this.closest('.product') || this.closest('.product__info-container') || document;
    }

    /**
     * Ensure all PDP add-to-cart form hidden inputs are active
     */
    enablePDPForms() {
      const scope = this.getScope();
      const idInputs = scope.querySelectorAll(
        'form[action*="/cart/add"] input[name="id"], product-form form input[name="id"]'
      );
      idInputs.forEach(input => {
        input.removeAttribute('disabled');
        input.disabled = false;
      });
    }

    /**
     * Synchronize the selected bundle variant with PDP elements
     */
    syncWithPDP() {
      const matchingVariant = this.findMatchingParentVariant();
      if (!matchingVariant) return;

      const scope = this.getScope();

      // 1. Update PDP Form <input name="id">
      const idInputs = scope.querySelectorAll(
        'form[action*="/cart/add"] input[name="id"], product-form form input[name="id"], .product-form form input[name="id"]'
      );
      idInputs.forEach(input => {
        input.value = matchingVariant.id;
        input.removeAttribute('disabled');
        input.disabled = false;
      });

      // 2. Update PDP Add to Cart Button(s)
      const atcButtons = scope.querySelectorAll(
        '.product-form__submit, [data-atc-notify-button], form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] button[name="add"], .sticky-cart button[name="add"]'
      );

      atcButtons.forEach(btn => {
        btn.dataset.variantId = matchingVariant.id;

        if (matchingVariant.available) {
          btn.removeAttribute('disabled');
          btn.disabled = false;
          btn.type = 'submit';
          btn.classList.remove('notify-mode');
          btn.dataset.notifyMode = 'false';

          const btnText = window.variantStrings?.addToCart || 'Add to cart';
          const textSpan = btn.querySelector('span');
          if (textSpan) {
            textSpan.textContent = btnText;
          } else {
            btn.textContent = btnText;
          }
        } else {
          btn.type = 'button';
          btn.classList.add('notify-mode');
          btn.dataset.notifyMode = 'true';

          const btnText = window.variantStrings?.soldOut || 'Notify Me';
          const textSpan = btn.querySelector('span');
          if (textSpan) {
            textSpan.textContent = btnText;
          } else {
            btn.textContent = btnText;
          }
        }
      });

      // 3. Update PDP Price Display
      this.updatePDPPrice(matchingVariant);

      // 4. Update browser URL without reloading (only if not in quick-view)
      if (!this.closest('.quick-view') && window.history && window.history.replaceState) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('variant', matchingVariant.id);
        window.history.replaceState({ path: newUrl.href }, '', newUrl.href);
      }

      // 5. Fire global change events for theme listeners
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: matchingVariant }
      }));
      document.dispatchEvent(new CustomEvent('bundle:variant-synced', {
        detail: {
          variant: matchingVariant,
          selections: this.currentSelections
        }
      }));
    }

    /**
     * Format money string
     */
    formatPrice(cents) {
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        const moneyFormat = window.theme.shopSettings?.moneyFormat || 'Rs. {{amount}}';
        return window.theme.Currency.formatMoney(cents, moneyFormat);
      }
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      return '₹' + (cents / 100).toFixed(0);
    }

    /**
     * Update price element on PDP
     */
    updatePDPPrice(variant) {
      const scope = this.getScope();
      const priceContainers = scope.querySelectorAll(
        `#price-${this.sectionId}, .product__info-container .price, .product__title + .price, [id^="price-"]`
      );

      priceContainers.forEach(container => {
        const regularPrice = container.querySelector('.price-item--regular, .price__regular .price-item');
        const salePrice = container.querySelector('.price-item--sale, .price__sale .price-item--last');
        const comparePrice = container.querySelector('.price__sale s .price-item, .price-item--sale s');

        if (variant.compare_at_price > variant.price) {
          container.classList.add('price--on-sale');
          if (salePrice) salePrice.innerHTML = this.formatPrice(variant.price);
          if (comparePrice) comparePrice.innerHTML = this.formatPrice(variant.compare_at_price);
        } else {
          container.classList.remove('price--on-sale');
          if (regularPrice) regularPrice.innerHTML = this.formatPrice(variant.price);
        }
      });
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
